import React, { useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Story } from '../../context/AppContext';
import { formatDurationLabel } from '../../utils/duration';
import StoryArtwork from '../StoryArtwork/StoryArtwork';
import StoryBackdrop from './StoryBackdrop';
import { StoryWorld } from './storyWorldModel';
import { ArrowLeftIcon, ArrowRightIcon } from '../icons';

interface Props {
  world: StoryWorld;
  selectedId?: string;
  onSelect: (story: Story) => void;
  action: (story: Story) => React.ReactNode;
  detail?: (story: Story) => React.ReactNode;
  status?: (story: Story) => React.ReactNode;
  duration?: (story: Story) => string | null;
  motion: boolean;
  first: boolean;
}

const StoryScene: React.FC<Props> = ({ world, selectedId, onSelect, action, detail, status, duration, motion, first }) => {
  const id = useId();
  const sceneRef = useRef<HTMLElement>(null);
  const infoRef = useRef<HTMLDialogElement>(null);
  const [infoOpen, setInfoOpen] = useState(false);
  const [nearby, setNearby] = useState(first);
  useEffect(() => {
    if (typeof IntersectionObserver === 'undefined') { setNearby(true); return; }
    const observer = new IntersectionObserver(([entry]) => setNearby(entry.isIntersecting), {
      root: sceneRef.current?.parentElement, rootMargin: `${sceneRef.current?.parentElement?.clientHeight || 0}px 0px`,
    });
    if (sceneRef.current) observer.observe(sceneRef.current);
    return () => observer.disconnect();
  }, []);
  useEffect(() => {
    if (!infoOpen) return;
    const previous = document.activeElement as HTMLElement | null;
    const dialog = infoRef.current;
    if (dialog?.showModal) dialog.showModal(); else dialog?.setAttribute('open', '');
    return () => { dialog?.close?.(); previous?.focus({ preventScroll: true }); };
  }, [infoOpen]);
  const current = world.chapters.find((chapter) => chapter.story_id === selectedId) || world.chapters[0];
  const index = world.chapters.indexOf(current);
  const touch = useRef<{ x: number; y: number } | null>(null);
  const trackRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const track = trackRef.current;
    const chip = track?.querySelector<HTMLElement>('[aria-pressed="true"]');
    if (!track || !chip) return;
    const left = chip.offsetLeft - track.offsetLeft;
    if (left < track.scrollLeft) track.scrollLeft = left;
    else if (left + chip.offsetWidth > track.scrollLeft + track.clientWidth) track.scrollLeft = left + chip.offsetWidth - track.clientWidth;
  }, [current.story_id]);
  const choose = (next: number) => {
    if (next >= 0 && next < world.chapters.length) onSelect(world.chapters[next]);
  };
  return <section ref={sceneRef} className="story-scene" data-world-id={world.id} aria-labelledby={id}
    onTouchStart={(event) => {
      if ((event.target as HTMLElement).closest('button,a,input,select,summary')) { touch.current = null; return; }
      touch.current = { x: event.changedTouches[0].clientX, y: event.changedTouches[0].clientY };
    }}
    onTouchEnd={(event) => {
      if (!touch.current) return;
      const dx = event.changedTouches[0].clientX - touch.current.x;
      const dy = event.changedTouches[0].clientY - touch.current.y;
      touch.current = null;
      if (world.serialized && Math.abs(dx) > 60 && Math.abs(dx) > Math.abs(dy) * 1.4) choose(index + (dx < 0 ? 1 : -1));
    }}
    onTouchCancel={() => { touch.current = null; }}>
    <StoryBackdrop src={nearby ? current.thumbnail_url : null} srcSet={nearby ? current.thumbnail_srcset : null} motion={motion} eager={nearby} />
    <div className="story-scene__content">
      <div className="story-scene__copy" tabIndex={0} aria-label="Story introduction">
      <p className="story-scene__eyebrow">{world.serialized ? world.chapters.length + ' chapters available' : current.theme || 'A bedtime adventure'}</p>
      <h2 id={id} tabIndex={-1}>{world.title}</h2>
      <div aria-live="polite" aria-atomic="true" className="story-scene__synopsis">
        {world.serialized && <h3>{current.chapter_number}. {current.chapter_title || current.title}</h3>}
        <p className="story-scene__summary">{current.description || current.summary || 'A story to share at bedtime.'}</p>
        <p className="story-scene__meta"><span>{duration?.(current) || formatDurationLabel(current.estimated_duration_minutes)}</span>{current.age_range && <span>Ages {current.age_range}</span>}{status?.(current)}</p>
      </div>
      </div>
      {!world.serialized && <button type="button" className="story-scene__info" onClick={(event) => { event.currentTarget.focus({ preventScroll: true }); setInfoOpen(true); }}>Read the summary</button>}
    </div>
    <div className="story-scene__actions">{action(current)}</div>
    {world.serialized && <div className="story-scene__chapters">
      <div className="story-scene__chapter-heading"><p><button type="button" className="story-scene__info" onClick={(event) => { event.currentTarget.focus({ preventScroll: true }); setInfoOpen(true); }}>About this book</button><span>{index + 1} / {world.chapters.length}</span></p><div>
        <button type="button" className="world-icon-button" aria-label={'Previous chapter of ' + world.title} disabled={index === 0} onClick={() => choose(index - 1)}><ArrowLeftIcon size={18} /></button>
        <button type="button" className="world-icon-button" aria-label={'Next chapter of ' + world.title} disabled={index === world.chapters.length - 1} onClick={() => choose(index + 1)}><ArrowRightIcon size={18} /></button>
      </div></div>
      <div ref={trackRef} className="story-scene__chapter-track" role="group" aria-label={'Chapters of ' + world.title} onKeyDown={(event) => {
        if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
        event.preventDefault();
        const next = event.key === 'Home' ? 0 : event.key === 'End' ? world.chapters.length - 1 : Math.max(0, Math.min(world.chapters.length - 1, index + (event.key === 'ArrowRight' ? 1 : -1)));
        choose(next);
        event.currentTarget.querySelectorAll<HTMLButtonElement>('button')[next]?.focus();
      }}>
        {world.chapters.map((chapter) => <button type="button" key={chapter.story_id} className="story-chapter" aria-pressed={chapter.story_id === current.story_id} onClick={() => onSelect(chapter)}>
          <StoryArtwork src={nearby ? chapter.thumbnail_url : null} srcSet={chapter.thumbnail_srcset} sizes="(max-width: 600px) 54px, 70px" loading="lazy" imageClassName="story-chapter__art" fallbackClassName="story-chapter__art" fallbackSize={20} />
          <span><small>Chapter {chapter.chapter_number}</small><strong>{chapter.chapter_title || chapter.title}</strong></span>
        </button>)}
      </div>
    </div>}
    {infoOpen && createPortal(<dialog ref={infoRef} className="story-search story-info" aria-labelledby={id + '-info'} onCancel={(event) => { event.preventDefault(); setInfoOpen(false); }} onClick={(event) => { if (event.target === event.currentTarget) setInfoOpen(false); }}>
      <div className="story-search__content"><header><h2 id={id + '-info'}>{world.title}</h2><button type="button" className="world-icon-button" aria-label="Close story information" onClick={() => setInfoOpen(false)}>×</button></header>
        {world.serialized && current.book_summary && <div className="story-scene__book-overview"><StoryArtwork src={current.book_thumbnail_url} srcSet={current.book_thumbnail_srcset} sizes="100px" loading="lazy" imageClassName="story-scene__book-cover" fallbackClassName="story-scene__book-cover" /><p>{current.book_summary}</p></div>}
        {world.serialized && <h3>{current.chapter_number}. {current.chapter_title || current.title}</h3>}
        <p>{current.description || current.summary}</p>
        {current.summary !== current.description && <p>{current.summary}</p>}
        {detail?.(current)}
      </div>
    </dialog>, document.body)}
  </section>;
};
export default StoryScene;
